import React from 'react';
import {
  IntegrationApiFeaturesServiceAvailabilityContractsResponsesGetServiceAvailabilityDetailsResponse as ServiceAvailabilityDetailsResponse,
  IntegrationApiFeaturesOutagesContractsResponsesSubscribeSmsServiceResponse as SubscribeSmsServiceResponse,
  IntegrationApiFeaturesOutagesContractsResponsesSubscribeSmsServiceOutageResponse as SubscribeSmsServiceOutageResponse,
  IntegrationApiFeaturesOutagesContractsDataOutage as SpecificOutageResponse,
} from '@alliander-fe/api';

import { UseMutationResult } from '@tanstack/react-query';
import { useTrackingContext } from 'src/context/TrackingContext';
import { handleApiResponses } from '../utils/handleApiResponses';
import { WIZARD_NAME } from '../constant/constant';

type WizardTrackingProps = {
  serviceAvailabilityDetails: UseMutationResult<ServiceAvailabilityDetailsResponse, Error>;
  subscribeSmsService: UseMutationResult<SubscribeSmsServiceResponse, Error>;
  subscribeSmsServiceOutage: UseMutationResult<SubscribeSmsServiceOutageResponse, Error>;
  specificOutageMutation: UseMutationResult<SpecificOutageResponse, Error>;
};

export const useWizardTracking = ({
  serviceAvailabilityDetails,
  subscribeSmsService,
  subscribeSmsServiceOutage,
  specificOutageMutation,
}: WizardTrackingProps) => {
  const { trackCustomWizard } = useTrackingContext();

  React.useEffect(() => {
    const {
      nrAlreadyExists,
      tooManyAddresses,
      isOperational,
      outageNoLongerExists,
      isIdle,
      isLoading,
      isSuccess,
    } = handleApiResponses({
      serviceAvailabilityDetails,
      subscribeSmsService,
      subscribeSmsServiceOutage,
      specificOutageMutation,
    });

    if (isIdle || isLoading) return;

    if (subscribeSmsService.isError) {
      trackCustomWizard({
        action: 'field validation error',
        actionDetail: subscribeSmsService.error.message,
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (specificOutageMutation.isError) {
      trackCustomWizard({
        action: 'field validation error',
        actionDetail: specificOutageMutation.error.message,
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (subscribeSmsServiceOutage.isError) {
      trackCustomWizard({
        action: 'field validation error',
        actionDetail: subscribeSmsServiceOutage.error.message,
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (nrAlreadyExists && !tooManyAddresses) {
      trackCustomWizard({
        action: 'proceed',
        actionDetail: 'Telefoonnummer al geregistreerd',
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (tooManyAddresses) {
      trackCustomWizard({
        action: 'proceed',
        actionDetail: 'Te veel inschrijvingen',
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (!isOperational) {
      trackCustomWizard({
        action: 'proceed',
        actionDetail: 'Adres buiten Liander gebied',
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (outageNoLongerExists) {
      trackCustomWizard({
        action: 'proceed',
        actionDetail: 'Storing is opgelost',
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }

    if (isSuccess) {
      trackCustomWizard({
        action: 'proceed',
        actionDetail: 'Succesvol aangemeld',
        wizardName: WIZARD_NAME,
        wizardStep: 2,
      });

      return;
    }
  }, [
    serviceAvailabilityDetails,
    subscribeSmsService,
    subscribeSmsServiceOutage,
    specificOutageMutation,
    trackCustomWizard,
  ]);
};
